import React, { useState, useEffect } from "react";
import billingService from "../../services/billingService";
import { organizationApiService } from "../../services/apiV2Service";
import { getCurrentUserOrganizationId } from "../host-experience/host-clients/utils/hostClientsUtils";
import SuccessAlert from "../settings/components/alerts/SuccessAlert";
import logger from "../../utils/logger";
import { showApiMessage } from "../../utils/messageHandler";
import { Tab, Tabs } from "@mui/material";
import InformationModal from "../../components/informationModal";
import LoadingSpinner from "../../components/LoadingSpinner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../shadcn/ui/dialog";
import { Checkbox } from "../../shadcn/ui/checkbox";
import { Button } from "../../shadcn/ui/button";
import { Input } from "../../shadcn/ui/input";
import { Label } from "../../shadcn/ui/label";
import { Textarea } from "../../shadcn/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../shadcn/ui/select";
import { useCurrencies } from "../../hooks/useCurrencies";
import Pagination from "../../components/Pagination";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../shadcn/ui/table";
import * as localService from "../../services/localService";
import { Building2 } from "lucide-react";

const Packages = () => {
  const { currencies } = useCurrencies();

  const [packages, setPackages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(12); // 12 items per page for grid layout

  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage] = useState("");

  // Business Rule: Get current user's role and organization
  const [userRole, setUserRole] = useState("");

  // Tab state for admin users
  const [activeTab, setActiveTab] = useState("STANDARD"); // Default to STANDARD for admin

  // Form state for creating/editing packages
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [originalPackageData, setOriginalPackageData] = useState(null); // Store original package for comparison

  // Delete confirmation dialog state
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState(null);
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    description: "",
    type: "CUSTOM", // Business rule: Host can only create CUSTOM
    version: "1.0",
    discountId: "",
    maximumServiceProvidersAllowed: 1,
    maximumTeamSize: 1,
    validity: "",
    validityTimeUnit: "MONTH",
    expiryDate: "",
    recurring: true,
    offerIds: [],
    orgTypeIds: [],
    customerOrgIds: [],
    providerId: 0,
    currencyId: "",
  });

  // Available offers and discounts for package creation
  const [availableOffers, setAvailableOffers] = useState([]);
  const [offerCurrentPage, setOfferCurrentPage] = useState(1);
  const [offerPageSize] = useState(50);
  const [offerTotalCount, setOfferTotalCount] = useState(0);
  const [availableDiscounts, setAvailableDiscounts] = useState([]);
  const [availableOrgTypes, setAvailableOrgTypes] = useState([]);

  // Search functionality
  const [offerSearchQuery, setOfferSearchQuery] = useState("");
  const [isSearchingOffers, setIsSearchingOffers] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [orgSearchQuery, setOrgSearchQuery] = useState("");
  const [orgSearchResults, setOrgSearchResults] = useState([]);
  const [isSearchingOrgs, setIsSearchingOrgs] = useState(false);
  const [showOrgSearch, setShowOrgSearch] = useState(false);

  // Customer organization search state (separate from provider search) - only used for CUSTOM packages
  const [_customerOrgSearchQuery, _setCustomerOrgSearchQuery] = useState("");
  const [_customerOrgSearchResults, _setCustomerOrgSearchResults] = useState([]);
  const [_isSearchingCustomerOrgs, _setIsSearchingCustomerOrgs] = useState(false);
  const [_showCustomerOrgSearch, _setShowCustomerOrgSearch] = useState(false);

  // Provider selection (affects available offers)
  const [selectedProviderId, setSelectedProviderId] = useState(null);
  const [selectedProviderOrg, setSelectedProviderOrg] = useState(null);

  // Load packages based on active tab
  const loadPackages = async packageType => {
    try {
      setIsLoading(true);

      const packagesResponse = await billingService.getPackages({
        type: packageType,
        page: currentPage,
        limit: pageSize,
      });

      if (packagesResponse.data) {
        const data = Array.isArray(packagesResponse.data.data) ? packagesResponse.data.data : [];
        setPackages(data);

        // Extract pagination info safely
        const pagination = packagesResponse.data.pagination || {};
        const total = parseInt(pagination.totalCount || pagination.total || data.length || 0, 10);
        setTotalCount(isNaN(total) ? 0 : total);
      }
    } catch (error) {
      logger.error("Error loading packages:", error);
      showApiMessage(error, "load packages");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      try {
        setIsLoading(true);

        // Get current user's role and organization
        const userInfo = localService.getUser();
        const role = userInfo?.organizationRole?.code || userInfo?.role || userInfo?.roleCode;
        const _orgId = userInfo?.organizationId || userInfo?.organization?.id;

        logger.log("User Info:", userInfo);
        logger.log("Detected Role:", role);
        logger.log("User Role State:", userRole);

        setUserRole(role);

        // Business Rule: Admin sees both STANDARD and CUSTOM in tabs
        setActiveTab("STANDARD");
        setCurrentPage(1); // Reset to first page on initial load
        await loadPackages("STANDARD"); // Load STANDARD packages by default

        // Load available offers and discounts for package creation
        await loadPackageDependencies();
      } catch (error) {
        logger.error("Error loading packages:", error);
        showApiMessage(error, "load packages");
      } finally {
        setIsLoading(false);
      }
    };

    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload packages when page changes (but not on initial mount)
  useEffect(() => {
    if (activeTab && currentPage > 0) {
      loadPackages(activeTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Handle tab change
  const handleTabChange = async tabType => {
    setActiveTab(tabType);
    setCurrentPage(1); // Reset to first page when switching tabs
    // Clear customer organization IDs when switching to STANDARD tab
    if (tabType === "STANDARD") {
      setFormData(prev => ({
        ...prev,
        customerOrgIds: [],
      }));
    }
    await loadPackages(tabType);
  };

  const loadPackageDependencies = async (page = 1) => {
    try {
      // Load offers (Business rule: Package has offers)
      const offersResponse = await billingService.getOffers({
        page: page,
        limit: offerPageSize,
      });
      if (offersResponse.data?.data) {
        // Start with unlinked offers only (null or 0)
        const unlinkedOffers = offersResponse.data.data.filter(
          offer => offer.organizationId === null || offer.organizationId === 0
        );
        logger.log("Available unlinked offers:", unlinkedOffers);
        logger.log("All offers from API:", offersResponse.data.data);
        setAvailableOffers(unlinkedOffers);

        // Extract pagination info
        const pagination = offersResponse.data.pagination || {};
        const totalCount = parseInt(
          pagination.totalCount || pagination.total || offersResponse.data.data.length || 0,
          10
        );
        setOfferTotalCount(isNaN(totalCount) ? 0 : totalCount);
      }

      // Load discounts (Business rule: Package may have discount)
      const discountsResponse = await billingService.getDiscounts({
        active: true,
      });
      if (discountsResponse.data?.data) {
        setAvailableDiscounts(discountsResponse.data.data);
      }

      // Load organization types using authenticated API
      logger.log("Loading organization types...");
      const orgTypesResponse = await organizationApiService.getOrganizationTypes();
      logger.log("Organization types response:", orgTypesResponse);
      if (orgTypesResponse.data?.data) {
        // Filter out admin organization types (users cannot assign packages to admin roles)
        const nonAdminOrgTypes = orgTypesResponse.data.data.filter(orgType => !orgType.admin);
        logger.log("Setting org types (filtered):", nonAdminOrgTypes);
        setAvailableOrgTypes(nonAdminOrgTypes);
      } else {
        logger.log("No org types data found in response");
      }
    } catch (error) {
      logger.error("Error loading package dependencies:", error);
    }
  };

  const _handleProviderChange = providerId => {
    setSelectedProviderId(providerId);
    setFormData({ ...formData, providerId: providerId || 0 });
    setOfferCurrentPage(1); // Reset to first page when provider changes

    // Reload offers based on provider selection according to backend rules
    loadOffersForProvider(providerId, 1);
  };

  const loadOffersForProvider = async providerId => {
    try {
      // Use a large limit to fetch all offers so client-side filtering works correctly
      const offersResponse = await billingService.getOffers({
        page: 1,
        limit: 500,
      });
      if (offersResponse.data?.data) {
        let filteredOffers;
        if (providerId) {
          // Show provider-specific offers AND universal offers
          filteredOffers = offersResponse.data.data.filter(
            offer => offer.organizationId === providerId || offer.organizationId === null || offer.organizationId === 0
          );
        } else {
          // No provider - show only unlinked offers (null or 0)
          filteredOffers = offersResponse.data.data.filter(
            offer => offer.organizationId === null || offer.organizationId === 0
          );
        }
        setAvailableOffers(filteredOffers);
        setOfferTotalCount(filteredOffers.length);
      }
    } catch (error) {
      logger.error("Error loading offers for provider:", error);
    }
  };

  const searchOffers = async (query, organizationId = null, page = 1) => {
    if (!query.trim()) {
      // If no query, load default offers
      setOfferCurrentPage(page);
      await loadOffersForProvider(organizationId, page);
      return;
    }

    try {
      setIsSearchingOffers(true);
      const searchParams = {
        q: query,
        limit: offerPageSize,
        page: page,
      };

      if (organizationId) {
        searchParams.organizationId = organizationId;
      }

      const searchResponse = await billingService.searchOffers(searchParams);
      if (searchResponse.data?.data) {
        setAvailableOffers(searchResponse.data.data);

        // Extract pagination info
        const pagination = searchResponse.data.pagination || {};
        const totalCount = parseInt(
          pagination.totalCount || pagination.total || searchResponse.data.data.length || 0,
          10
        );
        setOfferTotalCount(isNaN(totalCount) ? 0 : totalCount);
      }
    } catch (error) {
      logger.error("Error searching offers:", error);
      showApiMessage(error, "search offers");
    } finally {
      setIsSearchingOffers(false);
    }
  };

  // Replace user search with organization search for customer access control
  const searchUsers = async query => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setIsSearchingUsers(true);
      const searchParams = {
        q: query,
        limit: 20,
        page: 1,
      };

      const searchResponse = await organizationApiService.searchOrganizations(searchParams);
      if (searchResponse.data?.data) {
        setSearchResults(searchResponse.data.data);
      }
    } catch (error) {
      logger.error("Error searching organizations for customers:", error);
      showApiMessage(error, "search organizations");
    } finally {
      setIsSearchingUsers(false);
    }
  };

  const addUserToCustomerList = org => {
    const orgId = org.id;
    if (!formData.customerOrgIds.includes(orgId.toString())) {
      setFormData({
        ...formData,
        customerOrgIds: [...formData.customerOrgIds, orgId.toString()],
      });
    }
    setUserSearchQuery("");
    setSearchResults([]);
    setShowUserSearch(false);
  };

  const removeUserFromCustomerList = userId => {
    setFormData({
      ...formData,
      customerOrgIds: formData.customerOrgIds.filter(id => id !== userId.toString()),
    });
  };

  const searchOrganizations = async query => {
    if (!query.trim()) {
      setOrgSearchResults([]);
      return;
    }

    try {
      setIsSearchingOrgs(true);
      const currentOrgId = getCurrentUserOrganizationId();
      const searchParams = {
        q: query,
        limit: 20,
        page: 1,
      };

      const searchResponse = await organizationApiService.searchOrganizations(searchParams);
      if (searchResponse.data?.data) {
        // Filter out current user's organization in UI
        const filteredResults = searchResponse.data.data.filter(org => org.id !== currentOrgId);
        setOrgSearchResults(filteredResults);
      }
    } catch (error) {
      logger.error("Error searching organizations:", error);
      showApiMessage(error, "search organizations");
    } finally {
      setIsSearchingOrgs(false);
    }
  };

  const selectProviderOrganization = org => {
    setSelectedProviderId(org.id);

    // Clear incompatible offers when provider changes
    const compatibleOfferIds = formData.offerIds.filter(offerId => {
      const offer = availableOffers.find(o => o.id.toString() === offerId);
      return offer && (offer.organizationId === org.id || offer.organizationId === null || offer.organizationId === 0);
    });

    setFormData({
      ...formData,
      providerId: org.id,
      offerIds: compatibleOfferIds,
    });
    setOrgSearchQuery(`${org.name} (${org.code})`);
    setOrgSearchResults([]);
    setShowOrgSearch(false);
    // Reload offers for the selected provider
    loadOffersForProvider(org.id);
  };

  const clearProviderSelection = () => {
    setSelectedProviderId(null);

    // Clear all offers except universal ones when clearing provider
    const universalOfferIds = formData.offerIds.filter(offerId => {
      const offer = availableOffers.find(o => o.id.toString() === offerId);
      return offer && (offer.organizationId === null || offer.organizationId === 0);
    });

    setFormData({
      ...formData,
      providerId: 0,
      offerIds: universalOfferIds,
    });
    setOrgSearchQuery("");
    setOrgSearchResults([]);
    setShowOrgSearch(false);
    // Reload unlinked offers
    loadOffersForProvider(null);
  };

  const handleCreatePackage = async () => {
    try {
      setIsUpdating(true);

      // Validate required fields
      if (!formData.currencyId || formData.currencyId === "") {
        showApiMessage({ response: { status: 400, data: { message: "Please select a currency" } } }, "create package");
        throw new Error("Please select a currency");
      }

      // Allow users to create packages of any type, regardless of the active tab
      // The tab is just for filtering/viewing, not for restricting creation

      // Format expiryDate to YYYY-MM-DD if provided
      const formattedExpiryDate = formData.expiryDate
        ? new Date(formData.expiryDate).toISOString().split("T")[0]
        : undefined;

      const payload = {
        ...formData,
        // Note: amount field removed from create - only exists in update
        discountId: formData.discountId ? parseInt(formData.discountId) : undefined,
        expiryDate: formattedExpiryDate,
        // Handle validity: only include if recurring is true AND validity is provided
        validity:
          formData.recurring && formData.validity && formData.validity.trim() !== ""
            ? parseInt(formData.validity) || 1
            : formData.recurring
              ? null
              : undefined, // Don't include in payload if not recurring
        validityTimeUnit:
          formData.recurring && formData.validityTimeUnit
            ? formData.validityTimeUnit
            : formData.recurring
              ? null
              : undefined, // Don't include in payload if not recurring
        orgTypeIds: formData.orgTypeIds.map(id => parseInt(id)),
        currencyId: formData.currencyId ? parseInt(formData.currencyId) : undefined,
        // Only include customer organization IDs for CUSTOM packages
        ...(formData.type === "CUSTOM" && {
          customerOrgIds: formData.customerOrgIds.map(id => parseInt(id)),
        }),
        providerId: formData.providerId && formData.providerId !== 0 ? parseInt(formData.providerId) : null,
      };

      // Remove amount from payload (not in create schema)
      delete payload.amount;

      logger.log("Form data before submission:", formData);
      logger.log("Payload being sent:", payload);

      // Only include offerIds if there are offers selected
      if (formData.offerIds.length > 0) {
        payload.offerIds = formData.offerIds.map(id => parseInt(id));
        logger.log("Sending offerIds:", payload.offerIds);
        logger.log("Original formData.offerIds:", formData.offerIds);
      }

      // Admin uses /api/v1/packages endpoint
      const response = await billingService.createPackage(payload);

      if (response.data?.data) {
        // Show success message using messageHandler
        showApiMessage(response, "create package");
        setShowCreateForm(false);
        resetForm();

        // Refresh packages list for the current tab
        setCurrentPage(1); // Reset to first page after creating
        await loadPackages(activeTab);
      }
    } catch (error) {
      logger.error("Error creating package:", error);
      // Use messageHandler for consistent error handling
      showApiMessage(error, "create package");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdatePackage = async () => {
    logger.log("🚀 [DEBUG] handleUpdatePackage called!");
    logger.log("🚀 [DEBUG] formData:", formData);
    logger.log("🚀 [DEBUG] packages array:", packages);

    try {
      setIsUpdating(true);
      logger.log("🚀 [DEBUG] Starting update process...");

      // Validate required fields
      if (!formData.currencyId || formData.currencyId === "") {
        showApiMessage({ response: { status: 400, data: { message: "Please select a currency" } } }, "update package");
        throw new Error("Please select a currency");
      }

      // Format expiryDate to YYYY-MM-DD if provided
      const formattedExpiryDate = formData.expiryDate
        ? new Date(formData.expiryDate).toISOString().split("T")[0]
        : undefined;

      // Use the stored original package data (which has all the needed fields)
      logger.log("🔍 [DEBUG] Using stored originalPackageData:", originalPackageData);

      if (!originalPackageData) {
        logger.log("❌ [DEBUG] No original package data stored!");
        throw new Error("Original package data not found. Please close and reopen the edit form.");
      }

      logger.log("✅ [DEBUG] Original package found:", originalPackageData.name);

      // Calculate linked and unlinked organization types
      const originalOrgTypeIds = originalPackageData.orgTypes?.map(orgType => orgType.id) || [];
      const currentOrgTypeIds = formData.orgTypeIds.map(id => parseInt(id));

      const linkedOrgTypeIds = currentOrgTypeIds.filter(id => !originalOrgTypeIds.includes(id));
      const unlinkedOrgTypeIds = originalOrgTypeIds.filter(id => !currentOrgTypeIds.includes(id));

      // Calculate linked and unlinked offers
      const originalOfferIds = originalPackageData.offers?.map(offer => offer.id) || [];
      const currentOfferIds = formData.offerIds.map(id => parseInt(id));

      const linkedOfferIds = currentOfferIds.filter(id => !originalOfferIds.includes(id));
      const unlinkedOfferIds = originalOfferIds.filter(id => !currentOfferIds.includes(id));

      // Calculate linked and unlinked customer organizations (only for CUSTOM packages)
      let linkedCustomerOrgIds = [];
      let unlinkedCustomerOrgIds = [];

      if (originalPackageData.type === "CUSTOM") {
        const originalCustomerOrgIds = originalPackageData.customerOrgs?.map(org => org.id) || [];
        const currentCustomerOrgIds = formData.customerOrgIds.map(id => parseInt(id));
        linkedCustomerOrgIds = currentCustomerOrgIds.filter(id => !originalCustomerOrgIds.includes(id));
        unlinkedCustomerOrgIds = originalCustomerOrgIds.filter(id => !currentCustomerOrgIds.includes(id));
      }

      const payload = {
        code: formData.code,
        name: formData.name,
        description: formData.description,
        type: formData.type,
        version: formData.version,
        maximumServiceProvidersAllowed: formData.maximumServiceProvidersAllowed,
        maximumTeamSize: formData.maximumTeamSize,
        // Note: amount is NOT allowed in UPDATE per API validation (despite being in Swagger)
        // Handle validity: parse and include only if valid number is provided
        // Include validity if it's provided and not empty, regardless of recurring status
        validity:
          formData.validity && formData.validity.toString().trim() !== ""
            ? (() => {
                const parsed = parseInt(formData.validity);
                return isNaN(parsed) ? null : parsed;
              })()
            : null,
        validityTimeUnit: formData.validityTimeUnit || null,
        recurring: formData.recurring,
        currencyId: formData.currencyId ? parseInt(formData.currencyId) : undefined,
        linkedOrgTypeIds,
        unlinkedOrgTypeIds,
        linkedOfferIds,
        unlinkedOfferIds,
        // Only include customer organization linking for CUSTOM packages
        ...(originalPackageData.type === "CUSTOM" && {
          linkedCustomerOrgIds,
          unlinkedCustomerOrgIds,
        }),
      };

      // Add discount if provided
      if (formData.discountId) {
        payload.discountId = parseInt(formData.discountId);
      }

      // Add expiry date if provided
      if (formattedExpiryDate) {
        payload.expiryDate = formattedExpiryDate;
      }

      logger.log("🔍 [DEBUG] Package Update Calculation:");
      logger.log("Original org types:", originalOrgTypeIds);
      logger.log("Current org types:", currentOrgTypeIds);
      logger.log("Linked org types (new):", linkedOrgTypeIds);
      logger.log("Unlinked org types (removed):", unlinkedOrgTypeIds);
      logger.log("Original offers:", originalOfferIds);
      logger.log("Current offers:", currentOfferIds);
      logger.log("Linked offers (new):", linkedOfferIds);
      logger.log("Unlinked offers (removed):", unlinkedOfferIds);
      logger.log("Note: Customer organization linking not supported in update API");
      logger.log("Final payload:", payload);

      const response = await billingService.updatePackage(formData.id, payload);

      logger.log("🔍 [DEBUG] Update Package Response:", response);

      if (response.status === 200 || response.status === 204) {
        // Show success message using messageHandler
        showApiMessage(response, "update package");
        setShowEditForm(false);
        resetForm();

        // Refresh packages list for the current tab (keep current page)
        await loadPackages(activeTab);
      } else {
        throw new Error(`Update failed with status: ${response.status}`);
      }
    } catch (error) {
      logger.error("Error updating package:", error);
      // Use messageHandler for consistent error handling
      showApiMessage(error, "update package");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeletePackage = async packageId => {
    setPackageToDelete(packageId);
    setShowDeleteConfirmDialog(true);
  };

  const confirmDeletePackage = async () => {
    if (!packageToDelete) return;

    try {
      setIsUpdating(true);

      const response = await billingService.deletePackage(packageToDelete);

      // Show success message using messageHandler
      showApiMessage(response, "delete package");

      // If current page becomes empty, go to previous page
      if (packages.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        // Refresh packages list for the current tab
        await loadPackages(activeTab);
      }
    } catch (error) {
      logger.error("Error deleting package:", error);
      // Use messageHandler for consistent error handling
      showApiMessage(error, "delete package");
    } finally {
      setIsUpdating(false);
      setShowDeleteConfirmDialog(false);
      setPackageToDelete(null);
    }
  };

  const resetForm = () => {
    setFormData({
      code: "",
      name: "",
      description: "",
      type: "CUSTOM", // Default to CUSTOM for new packages
      version: "1.0",
      discountId: "",
      maximumServiceProvidersAllowed: 1,
      maximumTeamSize: 1,
      validity: "",
      validityTimeUnit: "MONTH",
      expiryDate: "",
      recurring: true,
      offerIds: [],
      orgTypeIds: [],
      customerOrgIds: [],
      providerId: 0,
      currencyId: "",
    });
    setOfferCurrentPage(1);
    setOfferSearchQuery("");

    // Reset provider selection
    setSelectedProviderId(null);
    setOrgSearchQuery("");
    setOrgSearchResults([]);
    setShowOrgSearch(false);
    setOriginalPackageData(null); // Clear original package data
    loadOffersForProvider(null); // Load unlinked offers
  };

  const openCreateForm = () => {
    resetForm();
    // Clear customer organization IDs for new packages based on current tab
    if (activeTab === "STANDARD") {
      setFormData(prev => ({
        ...prev,
        customerOrgIds: [],
      }));
    }
    setShowCreateForm(true);
    setShowEditForm(false);
  };

  const openEditForm = async pkg => {
    try {
      // Show the edit form with loading state
      setShowEditForm(true);
      setShowCreateForm(false);
      setIsUpdating(true); // Use this as loading state for the form

      // Reset form data first
      resetForm();

      // Fetch the latest package data from API
      const response = await billingService.getPackages({ id: pkg.id.toString() });

      if (response.data && response.data.data) {
        const packageData = response.data.data;

        // Store the original package data for comparison during update
        setOriginalPackageData(packageData);

        setFormData({
          id: packageData.id,
          code: packageData.code,
          name: packageData.name,
          description: packageData.description,
          type: packageData.type,
          version: packageData.version,
          discountId: packageData.discountId?.toString() || "",
          maximumServiceProvidersAllowed: packageData.maximumServiceProvidersAllowed,
          maximumTeamSize: packageData.maximumTeamSize,
          validity:
            packageData.validity != null && packageData.validity !== undefined ? packageData.validity.toString() : "",
          validityTimeUnit: packageData.validityTimeUnit || "MONTH",
          expiryDate: packageData.expiryDate || "",
          recurring: packageData.recurring,
          offerIds: packageData.offers?.map(offer => offer.id.toString()) || [],
          orgTypeIds: packageData.orgTypes?.map(orgType => orgType.id.toString()) || [],
          // Only load customer organization IDs for CUSTOM packages
          customerOrgIds:
            packageData.type === "CUSTOM" ? packageData.customerOrgs?.map(org => org.id.toString()) || [] : [],
          providerId: packageData.providerId || 0,
          currencyId: packageData.currencyId?.toString() || "",
        });

        // Determine provider from the package data and display it read-only
        if (packageData.providerId && packageData.providerId !== 0) {
          setSelectedProviderId(packageData.providerId);
          await loadOffersForProvider(packageData.providerId);
          if (packageData.provider) {
            setSelectedProviderOrg(packageData.provider);
            setOrgSearchQuery(`${packageData.provider.name} (${packageData.provider.code})`);
          } else {
            try {
              const orgResponse = await organizationApiService.getOrganizationById(packageData.providerId);
              const orgData = orgResponse.data?.data || orgResponse.data;
              if (orgData) {
                setSelectedProviderOrg(orgData);
                setOrgSearchQuery(`${orgData.name} (${orgData.code})`);
              }
            } catch {
              setSelectedProviderOrg(null);
            }
          }
        } else {
          setSelectedProviderId(null);
          setSelectedProviderOrg(null);
          await loadOffersForProvider(null);
          setOrgSearchQuery("");
        }
      } else {
        throw new Error("Package not found");
      }
    } catch (error) {
      logger.error("Error loading package for edit:", error);
      showApiMessage(error, "load package");
      setShowEditForm(false); // Close the form if loading fails
    } finally {
      setIsUpdating(false); // Hide loading state
    }
  };

  // Helper function to format currency and amount
  const formatCurrencyAmount = (currencyId, amount) => {
    if (!amount || amount === "0" || amount === 0) {
      return "N/A";
    }

    const currency = currencies.find(c => c.id === currencyId);
    if (currency) {
      const currencyCode = currency.code || "";
      // Format amount - remove trailing zeros if it's a whole number and add thousand separators
      const numAmount = parseFloat(amount);
      const formattedAmount =
        numAmount % 1 === 0
          ? numAmount.toLocaleString("en-US")
          : numAmount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return `${currencyCode} ${formattedAmount}`;
    }

    // Fallback if currency not found
    const numAmount = parseFloat(amount);
    const formattedAmount =
      numAmount % 1 === 0
        ? numAmount.toLocaleString("en-US")
        : numAmount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `USD ${formattedAmount}`;
  };

  // Helper function to format expiry date
  const formatExpiryDate = dateString => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    } catch (error) {
      return "N/A";
    }
  };

  if (isLoading) {
    return <LoadingSpinner loading={true} message="Loading packages..." padding="py-16" className="text-blue-600" />;
  }

  return (
    <div>
      <div style={{ position: "relative", paddingBottom: "6rem" }}>
        <div className="header" style={{ display: "flex", justifyContent: "space-between" }}>
          <div>
            <h3 style={{ fontSize: "1.6rem", fontWeight: "normal" }}>
              <b>Package Management</b>
            </h3>
          </div>
          <div>
            <Button onClick={openCreateForm} className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create Package
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <Tabs
            value={activeTab === "STANDARD" ? 0 : 1}
            onChange={(e, newValue) => handleTabChange(newValue === 0 ? "STANDARD" : "CUSTOM")}
            className="border-b"
          >
            <Tab label="Standard Packages" />
            <Tab label="Custom Packages" />
          </Tabs>
        </div>

        {/* Main Content Area */}
        <div className="px-6 py-8">
          {isLoading ? (
            <div className="flex justify-center items-center py-16">
              <div className="text-gray-500">Loading packages...</div>
            </div>
          ) : packages.length === 0 ? (
            <div className="py-16 text-center">
              <svg className="mx-auto w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No packages found</h3>
              <p className="mt-1 text-sm text-gray-500">No packages available in this category.</p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Expiry Date</TableHead>
                    <TableHead>Service Providers</TableHead>
                    <TableHead>Team Size</TableHead>
                    <TableHead>Validity</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {packages.map(pkg => (
                    <TableRow key={pkg.id}>
                      <TableCell className="font-mono text-sm">{pkg.code}</TableCell>
                      <TableCell className="font-medium">{pkg.name}</TableCell>
                      <TableCell className="max-w-xs truncate">{pkg.description || "N/A"}</TableCell>
                      <TableCell>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            pkg.type === "STANDARD" ? "bg-blue-100 text-blue-800" : "bg-purple-100 text-purple-800"
                          }`}
                        >
                          {pkg.type}
                        </span>
                      </TableCell>
                      <TableCell className="font-semibold">
                        {formatCurrencyAmount(pkg.currencyId, pkg.amount)}
                      </TableCell>
                      <TableCell>{formatExpiryDate(pkg.expiryDate)}</TableCell>
                      <TableCell>{pkg.maximumServiceProvidersAllowed}</TableCell>
                      <TableCell>{pkg.maximumTeamSize}</TableCell>
                      <TableCell>
                        {pkg.validity ? `${pkg.validity} ${pkg.validityTimeUnit?.toLowerCase() || ""}` : "N/A"}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button onClick={() => openEditForm(pkg)} variant="outline" size="sm" title="Edit Package">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                              />
                            </svg>
                          </Button>
                          <Button
                            onClick={() => handleDeletePackage(pkg.id)}
                            variant="destructive"
                            size="sm"
                            title="Delete Package"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {totalCount > 0 && (
            <div className="flex justify-center mt-6">
              <Pagination
                className="pagination-bar"
                currentPage={currentPage}
                totalCount={totalCount}
                pageSize={pageSize}
                siblingCount={1}
                onPageChange={({ selected }) => {
                  const page = parseInt(selected, 10);
                  if (!isNaN(page) && page > 0) {
                    setCurrentPage(page);
                  }
                }}
              />
            </div>
          )}
        </div>

        {/* Create/Edit Form Modal */}
        <Dialog
          open={showCreateForm || showEditForm}
          onOpenChange={() => {
            setShowCreateForm(false);
            setShowEditForm(false);
            resetForm();
          }}
        >
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">
                {showCreateForm ? "Create New Package" : "Edit Package"}
              </DialogTitle>
            </DialogHeader>

            {/* Loading spinner for edit form */}
            {showEditForm && isUpdating && (
              <div className="flex justify-center items-center py-8">
                <LoadingSpinner />
                <span className="ml-2 text-sm text-gray-600">Loading package data...</span>
              </div>
            )}

            {/* Show form only when not loading (for edit) or always (for create) */}
            {(showCreateForm || (showEditForm && !isUpdating)) && (
              <form
                onSubmit={e => {
                  e.preventDefault();
                  showCreateForm ? handleCreatePackage() : handleUpdatePackage();
                }}
              >
                <div className="grid grid-cols-1 gap-4 mb-4 md:grid-cols-2">
                  <div>
                    <Label htmlFor="package-code" className="text-sm font-medium">
                      Package Code *
                    </Label>
                    <Input
                      id="package-code"
                      type="text"
                      value={formData.code}
                      onChange={e => setFormData({ ...formData, code: e.target.value })}
                      className="mt-1"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="package-name" className="text-sm font-medium">
                      Package Name *
                    </Label>
                    <Input
                      id="package-name"
                      type="text"
                      value={formData.name}
                      onChange={e => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="package-type" className="text-sm font-medium">
                      Package Type *
                    </Label>
                    <Select value={formData.type} onValueChange={value => setFormData({ ...formData, type: value })}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select package type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CUSTOM">CUSTOM</SelectItem>
                        <SelectItem value="STANDARD">STANDARD</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {showCreateForm
                        ? `Note: When creating packages, the type should match the current tab (${activeTab})`
                        : "You can change the package type when editing"}
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="max-providers" className="text-sm font-medium">
                      Maximum Service Providers *
                    </Label>
                    <Input
                      id="max-providers"
                      type="number"
                      value={formData.maximumServiceProvidersAllowed}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          maximumServiceProvidersAllowed: parseInt(e.target.value),
                        })
                      }
                      className="mt-1"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="max-team-size" className="text-sm font-medium">
                      Maximum Team Size *
                    </Label>
                    <Input
                      id="max-team-size"
                      type="number"
                      value={formData.maximumTeamSize}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          maximumTeamSize: parseInt(e.target.value),
                        })
                      }
                      className="mt-1"
                      required
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <Label htmlFor="description" className="text-sm font-medium">
                    Description *
                  </Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="mt-1"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 mb-4 md:grid-cols-2">
                  <div>
                    <Label htmlFor="currency" className="text-sm font-medium">
                      Currency *
                    </Label>
                    <Select
                      value={formData.currencyId ? formData.currencyId.toString() : ""}
                      onValueChange={value => setFormData({ ...formData, currencyId: value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select Currency" />
                      </SelectTrigger>
                      <SelectContent>
                        {currencies && currencies.length > 0 ? (
                          currencies.map(currency => (
                            <SelectItem key={currency.id} value={currency.id.toString()}>
                              {currency.code} - {currency.name}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="" disabled>
                            Loading currencies...
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="discount" className="text-sm font-medium">
                      Discount (Optional)
                    </Label>
                    <Select
                      value={formData.discountId || "none"}
                      onValueChange={value =>
                        setFormData({
                          ...formData,
                          discountId: value === "none" ? "" : value,
                        })
                      }
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="No Discount" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Discount</SelectItem>
                        {availableDiscounts.map(discount => (
                          <SelectItem key={discount.id} value={discount.id.toString()}>
                            {discount.name} ({discount.percentage || discount.fixedValue})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="expiry-date" className="text-sm font-medium">
                      Expiry Date (Optional)
                    </Label>
                    <Input
                      id="expiry-date"
                      type="date"
                      value={formData.expiryDate}
                      onChange={e => setFormData({ ...formData, expiryDate: e.target.value })}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="validity" className="text-sm font-medium">
                      Validity *
                    </Label>
                    <Input
                      id="validity"
                      type="number"
                      min="1"
                      value={formData.validity}
                      onChange={e => setFormData({ ...formData, validity: e.target.value })}
                      className="mt-1"
                      placeholder="Enter validity period"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="validity-unit" className="text-sm font-medium">
                      Validity Time Unit
                    </Label>
                    <Select
                      value={formData.validityTimeUnit}
                      onValueChange={value => setFormData({ ...formData, validityTimeUnit: value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select time unit" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="HOUR">Hours</SelectItem>
                        <SelectItem value="DAY">Days</SelectItem>
                        <SelectItem value="MONTH">Months</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Provider Organization Selection - Only show for CREATE */}
                {showCreateForm && (
                  <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h6 className="font-medium text-blue-900 mb-2">Provider Organization Selection</h6>
                    <p className="text-sm text-blue-700 mb-3">
                      {selectedProviderId
                        ? `Selected provider will show their offers + universal offers`
                        : "No provider selected - only universal offers available"}
                    </p>

                    {/* Organization Search Input */}
                    <div className="mb-2">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={orgSearchQuery}
                          onChange={e => {
                            setOrgSearchQuery(e.target.value);
                            // Debounce search
                            clearTimeout(window.orgSearchTimeout);
                            window.orgSearchTimeout = setTimeout(() => {
                              searchOrganizations(e.target.value);
                            }, 300);
                          }}
                          onFocus={() => setShowOrgSearch(true)}
                          placeholder="Search organizations by name or code..."
                          className="flex-1 px-3 py-2 bg-white rounded-md border border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => setShowOrgSearch(!showOrgSearch)}
                          className="px-3 py-2 text-sm bg-white text-blue-700 rounded-md hover:bg-blue-50 transition-colors border border-blue-300"
                        >
                          {showOrgSearch ? "Hide" : "Show"} Search
                        </button>
                        {selectedProviderId && (
                          <button
                            type="button"
                            onClick={clearProviderSelection}
                            className="px-3 py-2 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors border border-red-300"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      {isSearchingOrgs && <p className="mt-1 text-xs text-blue-600">Searching organizations...</p>}
                    </div>

                    {/* Search Results Dropdown */}
                    {showOrgSearch && orgSearchResults.length > 0 && (
                      <div className="mb-2 max-h-40 overflow-y-auto border border-blue-200 rounded-md bg-white shadow-lg">
                        {orgSearchResults.map(org => (
                          <div
                            key={org.id}
                            onClick={() => selectProviderOrganization(org)}
                            className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-blue-100 last:border-b-0"
                          >
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="text-sm font-medium text-gray-900">{org.name}</p>
                                <p className="text-xs text-gray-500">
                                  {org.code} • ID: {org.id}
                                </p>
                              </div>
                              <span className="text-xs text-blue-600">Select</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Selected Organization Display */}
                    {selectedProviderId && selectedProviderOrg && (
                      <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center gap-3">
                          {selectedProviderOrg.logoUrl ? (
                            <img
                              src={selectedProviderOrg.logoUrl}
                              alt={selectedProviderOrg.name}
                              className="w-10 h-10 rounded-lg object-cover"
                              onError={e => {
                                e.target.style.display = "none";
                              }}
                            />
                          ) : (
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-blue-600" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-blue-900 truncate">{selectedProviderOrg.name}</p>
                            <p className="text-xs text-blue-700">
                              {selectedProviderOrg.code} {selectedProviderOrg.contactEmail ? `• ` : ""}
                              {selectedProviderOrg.contactEmail ? (
                                <a href={`mailto:${selectedProviderOrg.contactEmail}`} className="hover:underline">
                                  {selectedProviderOrg.contactEmail}
                                </a>
                              ) : (
                                ""
                              )}
                            </p>
                          </div>
                        </div>
                        {selectedProviderOrg.contactMsisdn && (
                          <p className="text-xs text-blue-600 mt-1 ml-[3.25rem]">
                            <a href={`tel:+${selectedProviderOrg.contactMsisdn}`} className="hover:underline">
                              +{selectedProviderOrg.contactMsisdn}
                            </a>
                          </p>
                        )}
                      </div>
                    )}

                    {selectedProviderId && !selectedProviderOrg && (
                      <div className="mt-2 p-2 bg-blue-100 rounded-md">
                        <p className="text-sm text-blue-800">
                          <strong>Selected Provider:</strong> {orgSearchQuery}
                        </p>
                        <p className="text-xs text-blue-600">Showing offers from organization {selectedProviderId}</p>
                      </div>
                    )}

                    {!selectedProviderId && (
                      <div className="mt-2 p-2 bg-gray-100 rounded-md">
                        <p className="text-sm text-gray-600">No provider selected - showing only unlinked offers</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Provider Organization Info (Edit Mode - Read Only) */}
                {showEditForm && selectedProviderOrg && (
                  <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h6 className="font-medium text-blue-900 mb-3">Provider Organization</h6>
                    <div className="flex items-center gap-3">
                      {selectedProviderOrg.logoUrl ? (
                        <img
                          src={selectedProviderOrg.logoUrl}
                          alt={selectedProviderOrg.name}
                          className="w-10 h-10 rounded-lg object-cover"
                          onError={e => {
                            e.target.style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-blue-600" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-blue-900 truncate">{selectedProviderOrg.name}</p>
                        <p className="text-xs text-blue-700">
                          {selectedProviderOrg.code}{" "}
                          {selectedProviderOrg.contactEmail ? `• ${selectedProviderOrg.contactEmail}` : ""}
                        </p>
                      </div>
                    </div>
                    {selectedProviderOrg.contactMsisdn && (
                      <p className="text-xs text-blue-600 mt-1 ml-[3.25rem]">
                        <a href={`tel:+${selectedProviderOrg.contactMsisdn}`} className="hover:underline">
                          +{selectedProviderOrg.contactMsisdn}
                        </a>
                      </p>
                    )}
                  </div>
                )}

                {/* Service Offers */}
                {(showCreateForm || showEditForm) && (
                  <div className="mb-4 p-4 bg-green-50 rounded-lg border border-green-200">
                    <Label className="text-sm font-medium text-green-900">Service Offers *</Label>
                    <p className="text-sm text-green-700 mb-3">
                      {selectedProviderId
                        ? `Available offers from ${orgSearchQuery} + universal offers`
                        : "Universal offers available to all organizations"}
                    </p>

                    {/* Search Input for Offers */}
                    <div className="mb-2">
                      <Input
                        type="text"
                        value={offerSearchQuery}
                        onChange={e => {
                          setOfferSearchQuery(e.target.value);
                          setOfferCurrentPage(1); // Reset to first page on search
                          // Debounce search
                          clearTimeout(window.offerSearchTimeout);
                          window.offerSearchTimeout = setTimeout(() => {
                            searchOffers(e.target.value, selectedProviderId, 1);
                          }, 300);
                        }}
                        placeholder="Search offers by name or code..."
                        className="mt-1"
                      />
                      {isSearchingOffers && <p className="mt-1 text-xs text-green-600">Searching offers...</p>}
                    </div>

                    <div className="mt-2 space-y-2 max-h-40 overflow-y-auto border rounded-md p-3 bg-white">
                      {availableOffers.length === 0 ? (
                        <div className="text-center py-4 text-gray-500">
                          <p className="text-sm">No offers available</p>
                          <p className="text-xs mt-1">
                            {selectedProviderId
                              ? "Try selecting a different provider or search for offers"
                              : "Select a provider to see their offers"}
                          </p>
                        </div>
                      ) : (
                        availableOffers.map(offer => {
                          const isProviderOffer = offer.organizationId === selectedProviderId;
                          const isUniversalOffer = offer.organizationId === null || offer.organizationId === 0;
                          const isCompatible = isProviderOffer || isUniversalOffer;

                          return (
                            <div
                              key={offer.id}
                              className={`flex items-center space-x-2 p-2 rounded-md ${isCompatible ? "bg-green-50" : "bg-red-50 opacity-50"}`}
                            >
                              <Checkbox
                                id={`offer-${offer.id}`}
                                checked={formData.offerIds.includes(offer.id.toString())}
                                disabled={!isCompatible}
                                onCheckedChange={checked => {
                                  if (checked && isCompatible) {
                                    setFormData({
                                      ...formData,
                                      offerIds: [...formData.offerIds, offer.id.toString()],
                                    });
                                  } else {
                                    setFormData({
                                      ...formData,
                                      offerIds: formData.offerIds.filter(id => id !== offer.id.toString()),
                                    });
                                  }
                                }}
                              />
                              <Label
                                htmlFor={`offer-${offer.id}`}
                                className={`text-sm font-normal cursor-pointer flex-1 ${isCompatible ? "cursor-pointer" : "cursor-not-allowed"}`}
                              >
                                <div>
                                  <span className="font-medium">{offer.name}</span>
                                  <span className="text-muted-foreground"> - {offer.description}</span>
                                  <div className="flex items-center gap-2 mt-1">
                                    {isProviderOffer && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        Provider Offer
                                      </span>
                                    )}
                                    {isUniversalOffer && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        Universal Offer
                                      </span>
                                    )}
                                    {!isCompatible && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                        Incompatible
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </Label>
                            </div>
                          );
                        })
                      )}
                    </div>
                    {offerTotalCount > offerPageSize && (
                      <div className="mt-3 flex justify-center">
                        <Pagination
                          currentPage={offerCurrentPage}
                          totalCount={offerTotalCount}
                          pageSize={offerPageSize}
                          onPageChange={({ selected }) => {
                            setOfferCurrentPage(selected);
                            if (offerSearchQuery.trim()) {
                              searchOffers(offerSearchQuery, selectedProviderId, selected);
                            }
                          }}
                        />
                      </div>
                    )}
                    <p className="mt-2 text-xs text-green-600">
                      {selectedProviderId
                        ? "Provider offers are exclusive to this organization. Universal offers work with any provider."
                        : "Universal offers are available to all organizations and work with any provider."}
                    </p>
                  </div>
                )}

                {/* Organization Types Selection */}
                <div className="mb-4">
                  <Label className="text-sm font-medium">Organization Types *</Label>
                  {showEditForm && (
                    <p className="text-xs text-blue-600 mb-2">
                      Note: For updates, selected types will be linked to the package. Unselected types that were
                      previously linked will be unlinked.
                    </p>
                  )}
                  <div className="mt-2 space-y-2 max-h-32 overflow-y-auto border rounded-md p-3">
                    {logger.log("Available org types for rendering:", availableOrgTypes)}
                    {availableOrgTypes.length === 0 && (
                      <p className="text-sm text-muted-foreground">Loading organization types...</p>
                    )}
                    {availableOrgTypes.map(orgType => (
                      <div key={orgType.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`org-type-${orgType.id}`}
                          checked={formData.orgTypeIds.includes(orgType.id.toString())}
                          onCheckedChange={checked => {
                            if (checked) {
                              setFormData({
                                ...formData,
                                orgTypeIds: [...formData.orgTypeIds, orgType.id.toString()],
                              });
                            } else {
                              setFormData({
                                ...formData,
                                orgTypeIds: formData.orgTypeIds.filter(id => id !== orgType.id.toString()),
                              });
                            }
                          }}
                        />
                        <Label htmlFor={`org-type-${orgType.id}`} className="text-sm font-normal cursor-pointer">
                          {orgType.name} ({orgType.code})
                        </Label>
                      </div>
                    ))}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Select which organization types can access this package.
                  </p>
                </div>

                {/* Customer Organizations Search - Only show for CUSTOM packages */}
                {(showCreateForm || showEditForm) && activeTab === "CUSTOM" && (
                  <div className="mb-4">
                    <label className="block mb-1 text-sm font-medium text-gray-700">
                      Customer Organizations (Optional)
                    </label>

                    {/* Search Input for Users */}
                    <div className="mb-2">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={userSearchQuery}
                          onChange={e => {
                            setUserSearchQuery(e.target.value);
                            // Debounce search
                            clearTimeout(window.userSearchTimeout);
                            window.userSearchTimeout = setTimeout(() => {
                              searchUsers(e.target.value);
                            }, 300);
                          }}
                          onFocus={() => setShowUserSearch(true)}
                          placeholder="Search organizations by name or code..."
                          className="flex-1 px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => setShowUserSearch(!showUserSearch)}
                          className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                        >
                          {showUserSearch ? "Hide" : "Show"} Search
                        </button>
                      </div>
                      {isSearchingUsers && <p className="mt-1 text-xs text-blue-600">Searching users...</p>}
                    </div>

                    {/* Search Results Dropdown */}
                    {showUserSearch && searchResults.length > 0 && (
                      <div className="mb-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md bg-white shadow-lg">
                        {searchResults.map(org => (
                          <div
                            key={org.id}
                            onClick={() => addUserToCustomerList(org)}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="text-sm font-medium text-gray-900">{org.name}</p>
                                <p className="text-xs text-gray-500">
                                  {org.code} • ID: {org.id}
                                </p>
                              </div>
                              <span className="text-xs text-blue-600">Add</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Selected Organizations List */}
                    {formData.customerOrgIds.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-500 mb-1">Selected Organizations:</p>
                        <div className="flex flex-wrap gap-1">
                          {formData.customerOrgIds.map(userId => {
                            const org = searchResults.find(u => u.id.toString() === userId) || {
                              id: userId,
                              name: "Organization",
                              code: userId,
                            };
                            return (
                              <span
                                key={userId}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                              >
                                {org.name} ({org.code})
                                <button
                                  type="button"
                                  onClick={() => removeUserFromCustomerList(userId)}
                                  className="ml-1 text-blue-600 hover:text-blue-800"
                                >
                                  ×
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <p className="mt-1 text-xs text-gray-500">
                      Search and select specific customer organizations that can access this package. Leave empty for
                      all organizations of selected types.
                    </p>
                  </div>
                )}

                <div className="flex items-center mb-4">
                  <Checkbox
                    id="recurring"
                    checked={formData.recurring}
                    onCheckedChange={checked => setFormData({ ...formData, recurring: checked })}
                  />
                  <Label htmlFor="recurring" className="text-sm font-medium ml-2 cursor-pointer">
                    Recurring Billing
                  </Label>
                </div>

                <DialogFooter className="flex justify-end space-x-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowCreateForm(false);
                      setShowEditForm(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isUpdating} variant="default">
                    {isUpdating ? "Saving..." : showCreateForm ? "Create Package" : "Update Package"}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Alerts */}
        {showSuccessAlert && <SuccessAlert message={successMessage} onClose={() => setShowSuccessAlert(false)} />}

        {/* Delete Confirmation Dialog */}
        <InformationModal
          isOpen={showDeleteConfirmDialog}
          isClose={() => {
            setShowDeleteConfirmDialog(false);
            setPackageToDelete(null);
          }}
          onSubmit={confirmDeletePackage}
        >
          Are you sure you want to delete this package? This action cannot be undone.
        </InformationModal>
      </div>
    </div>
  );
};

export default Packages;
